import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Download, X } from "lucide-react";

const DISMISS_KEY = "bk_install_dismissed_at";
const DISMISS_TTL_MS = 1000 * 60 * 60 * 24 * 14; // 14 days
const SHOW_AFTER_MS = 2500;

const isMobileViewport = () =>
  typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches;

const isIOS = () =>
  typeof navigator !== "undefined" &&
  /iPad|iPhone|iPod/.test(navigator.userAgent) &&
  !window.MSStream;

const isStandalone = () =>
  typeof window !== "undefined" &&
  (window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true);

const dismissedRecently = () => {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    return Date.now() - Number(raw) < DISMISS_TTL_MS;
  } catch {
    return false;
  }
};

/**
 * Floating mini-CTA for PWA install.
 * - Android/Chromium: uses the native `beforeinstallprompt` event.
 * - iOS Safari: shows a lightbox with the "Ajouter à l'écran d'accueil" tutorial.
 * - Hidden in standalone/desktop/after dismissal (14 days).
 */
export const InstallAppButton = () => {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (isStandalone() || !isMobileViewport() || dismissedRecently()) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS never fires beforeinstallprompt — show the CTA anyway with a tutorial.
    let iosTimer;
    if (isIOS()) {
      iosTimer = setTimeout(() => setVisible(true), SHOW_AFTER_MS);
    }

    const onInstalled = () => {
      setVisible(false);
      try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  useEffect(() => {
    if (visible) {
      const t = setTimeout(() => setEntered(true), 20);
      return () => clearTimeout(t);
    }
    setEntered(false);
  }, [visible]);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    setVisible(false);
    setShowIosHelp(false);
  };

  const handleClick = async () => {
    if (deferred) {
      try {
        deferred.prompt();
        const { outcome } = await deferred.userChoice;
        if (outcome === "accepted" || outcome === "dismissed") {
          setDeferred(null);
          setVisible(false);
          if (outcome === "dismissed") dismiss();
        }
      } catch {
        setVisible(false);
      }
      return;
    }
    if (isIOS()) {
      setShowIosHelp(true);
      return;
    }
    dismiss();
  };

  if (!visible) return null;

  return createPortal(
    <>
      <button
        onClick={handleClick}
        aria-label="Installer le glossaire Beckyliss"
        data-testid="install-app-button"
        style={{
          position: "fixed",
          right: 16,
          bottom: 84, // sits above the "Retour à la boutique" pill on mobile
          zIndex: 45,
          display: "inline-flex",
          alignItems: "center",
          gap: 10,
          padding: "10px 14px 10px 12px",
          background: "linear-gradient(135deg, #F5CE6F 0%, #E5B24A 100%)",
          color: "#3A2A0F",
          border: "1px solid rgba(160, 120, 40, 0.25)",
          borderRadius: 999,
          fontFamily: "'Poppins', system-ui, sans-serif",
          fontSize: 13,
          fontWeight: 600,
          letterSpacing: ".005em",
          boxShadow:
            "0 12px 28px -6px rgba(160, 120, 40, 0.45), 0 2px 6px rgba(45,27,46,0.06)",
          cursor: "pointer",
          transition:
            "opacity .45s ease, transform .5s cubic-bezier(.2,.7,.3,1)",
          opacity: entered ? 1 : 0,
          transform: entered ? "translateY(0)" : "translateY(24px)",
        }}
      >
        <span
          aria-hidden
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 26,
            height: 26,
            borderRadius: 999,
            background: "rgba(255,255,255,0.6)",
          }}
        >
          <Download size={14} strokeWidth={2} />
        </span>
        Installer l'app
        <button
          onClick={(e) => { e.stopPropagation(); dismiss(); }}
          aria-label="Masquer"
          data-testid="install-app-dismiss"
          style={{
            all: "unset",
            marginLeft: 4,
            width: 20,
            height: 20,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 999,
            cursor: "pointer",
            color: "#5A3E10",
            opacity: 0.7,
          }}
        >
          <X size={13} strokeWidth={2.2} />
        </button>
      </button>

      {showIosHelp && (
        <div
          onClick={() => setShowIosHelp(false)}
          role="dialog"
          aria-modal="true"
          data-testid="install-ios-help"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 60,
            background: "rgba(58, 46, 58, 0.45)",
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "center",
            padding: 16,
            backdropFilter: "blur(4px)",
            WebkitBackdropFilter: "blur(4px)",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#FDF8F4",
              borderRadius: 20,
              padding: "22px 22px 18px",
              maxWidth: 340,
              width: "100%",
              boxShadow: "0 24px 60px -12px rgba(45,27,46,0.35)",
              fontFamily: "'Poppins', system-ui, sans-serif",
              color: "#2D1B2E",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, color: "#9B4A6B" }}>
                Ajouter à l'écran d'accueil
              </span>
              <button
                onClick={() => setShowIosHelp(false)}
                style={{ all: "unset", cursor: "pointer", color: "#8B7B8E" }}
                aria-label="Fermer"
              >
                <X size={18} />
              </button>
            </div>
            <p style={{ fontSize: 13, color: "#6E5C6E", lineHeight: 1.5, margin: "6px 0 14px" }}>
              Pour installer le glossaire en une icône sur ton iPhone :
            </p>
            <ol style={{ paddingLeft: 20, fontSize: 13.5, lineHeight: 1.65, color: "#3A2E3A", margin: 0 }}>
              <li>Touche l'icône <b>Partager</b> en bas de Safari.</li>
              <li>Fais défiler et choisis <b>« Sur l'écran d'accueil »</b>.</li>
              <li>Valide avec <b>Ajouter</b> — le logo doré apparaît sur ton écran.</li>
            </ol>
            <button
              onClick={dismiss}
              style={{
                marginTop: 16,
                width: "100%",
                padding: "10px 12px",
                background: "linear-gradient(135deg, #F5CE6F 0%, #E5B24A 100%)",
                color: "#3A2A0F",
                border: "none",
                borderRadius: 999,
                fontSize: 13.5,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              J'ai compris
            </button>
          </div>
        </div>
      )}
    </>,
    document.body
  );
};

export default InstallAppButton;
